import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, MapPin, Search, Truck,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ORDER_STATUS } from '../../api/orders';
import { listDeliveryOrders } from '../../api/delivery';
import EmptyState from '../../components/EmptyState';
import Button from '../../components/Button';
import DriverShell from './DriverShell';
import cardStyles from './DriverCards.module.css';
import styles from './DriverRideHistory.module.css';

const FILTERS = [
  { key: 'ALL', label: 'All' },
  { key: ORDER_STATUS.DELIVERED, label: 'Delivered' },
  { key: ORDER_STATUS.FAILED_DELIVERY, label: 'Failed' },
];

const STAGE_META = {
  [ORDER_STATUS.DELIVERED]: { label: 'Delivered', fg: '#2E7D32', bg: '#E3F1E4' },
  [ORDER_STATUS.FAILED_DELIVERY]: { label: 'Failed', fg: '#D32F2F', bg: '#FBE9E9' },
};

function formatWhen(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

export default function DriverRideHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('ALL');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listDeliveryOrders(`${ORDER_STATUS.DELIVERED},${ORDER_STATUS.FAILED_DELIVERY}`, user.idToken);
      setRides(list);
    } catch (err) {
      setError(err.message || 'Could not load your ride history.');
    } finally {
      setLoading(false);
    }
  }, [user.idToken]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRides = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rides
      .filter((o) => filter === 'ALL' || o.status === filter)
      .filter((o) => !q
        || o.id.toLowerCase().includes(q)
        || (o.customer.name || '').toLowerCase().includes(q)
        || (o.address || '').toLowerCase().includes(q))
      .sort((a, b) => new Date(b.placedAt) - new Date(a.placedAt));
  }, [rides, search, filter]);

  return (
    <DriverShell>
      <header className={cardStyles.header}>
        <button className={cardStyles.backBtn} onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <h1 className={cardStyles.headerTitle}>Ride History</h1>
      </header>

      <main className={styles.content}>
        <div className={styles.searchWrap}>
          <Search size={15} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search by customer, order ID or address"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className={styles.filterRow}>
          {FILTERS.map(({ key, label }) => (
            <button
              type="button"
              key={key}
              className={`${styles.filterPill} ${filter === key ? styles.filterPillActive : ''}`}
              onClick={() => setFilter(key)}
            >
              {filter === key && <span className={styles.filterCheck}>✓</span>}
              {label}
            </button>
          ))}
        </div>

        {loading && <p>Loading ride history…</p>}

        {!loading && error && (
          <EmptyState
            icon={AlertTriangle}
            title="Could not load ride history"
            description={error}
            action={<Button variant="outline" onClick={load}>Retry</Button>}
          />
        )}

        {!loading && !error && visibleRides.length === 0 && (
          <EmptyState
            icon={Truck}
            title="No rides found"
            description="Try a different search or filter."
          />
        )}

        {!loading && !error && visibleRides.length > 0 && (
          <div className={styles.rideList}>
            {visibleRides.map((order) => {
              const meta = STAGE_META[order.status] || { label: order.status, fg: '#5B6675', bg: '#EEF1F5' };
              return (
                <button
                  type="button"
                  className={styles.rideCard}
                  key={order.id}
                  onClick={() => navigate(`/driver/rides/${order.id}`, { state: { order } })}
                >
                  <div className={styles.rideTop}>
                    <span className={styles.rideId}>{order.id}</span>
                    <span className={styles.stageBadge} style={{ color: meta.fg, background: meta.bg }}>
                      {meta.label}
                    </span>
                  </div>

                  <p className={styles.rideCustomer}>{order.customer.name || 'Customer'}</p>
                  {order.address && (
                    <p className={styles.rideAddress}>
                      <MapPin size={12} /> {order.address}
                    </p>
                  )}
                  <p className={styles.rideTime}>{formatWhen(order.updatedAt || order.placedAt)}</p>
                </button>
              );
            })}
          </div>
        )}
      </main>
    </DriverShell>
  );
}
